import React from "react";
import { BRAND } from "@/lib/data";
import {
  CalendarCheck,
  Phone,
  Clock,
  Sparkles,
  CreditCard,
  Briefcase,
  FileText,
  MapPin,
} from "lucide-react";
import useSeo from "@/hooks/useSeo";
import LazyIframe from "@/components/LazyIframe";

const CATEGORIES = [
  {
    icon: Sparkles,
    color: "bg-gold",
    label: "Start Here",
    title: "Not sure where to begin",
    body: "Free 15-min call with Devyne to figure out the right service for you.",
  },
  {
    icon: CreditCard,
    color: "bg-navy",
    label: "Credit & Money",
    title: "Credit repair, mindset, business credit",
    body: "Anew Credit Repair tiers, Pay-Per-Deletion, Money Mindset Reset™, Business Credit Starter.",
  },
  {
    icon: Briefcase,
    color: "bg-teal",
    label: "Business & Bookkeeping",
    title: "Form, run, and grow your business",
    body: "LLC formation, monthly bookkeeping, consulting (60 or 90 min), business credit.",
  },
  {
    icon: FileText,
    color: "bg-navy",
    label: "Tax Services",
    title: "Personal, self-employed, and business taxes",
    body: "W-2 prep, Schedule C self-employed prep, LLC / S-Corp / Partnership returns.",
  },
  {
    icon: MapPin,
    color: "bg-gold",
    label: "Notary & Travel",
    title: "Notary and travel planning",
    body: "Mobile + in-office notary, loan signing, Fora-partnered travel planning.",
  },
];

export default function BookPage() {
  useSeo({
    title: "Book a Consultation",
    description:
      "Schedule with ConnectHER — credit repair, taxes, bookkeeping, business consulting, notary, or travel planning. Free 15-minute discovery calls available.",
    path: "/book",
  });
  return (
    <>
      <section className="pt-16 md:pt-24 pb-10" data-testid="book-hero">
        <div className="container-app max-w-4xl text-center">
          <div className="eyebrow">Book an Appointment</div>
          <h1 className="mt-3 text-5xl md:text-6xl leading-tight">
            Let&apos;s get on the calendar.
          </h1>
          <p className="mt-5 text-lg text-navy/70 max-w-2xl mx-auto leading-relaxed">
            Choose the service category that matches your goal, then pick a
            time that works for you. Free 15-minute discovery calls available
            for every service. No pressure — just clarity.
          </p>
        </div>
      </section>

      <section className="pb-12" data-testid="book-categories">
        <div className="container-app">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {CATEGORIES.map((c, i) => (
              <div
                key={c.label}
                className="card p-5 hover:-translate-y-1 transition-transform"
                data-testid={`book-category-${c.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")
                  .replace(/&/g, "and")}`}
              >
                <div
                  className={`w-10 h-10 rounded-xl ${c.color} text-white flex items-center justify-center mb-3`}
                >
                  <c.icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-gold">
                  {c.label}
                </div>
                <h4 className="mt-1 text-base font-serif text-navy leading-tight">
                  {c.title}
                </h4>
                <p className="mt-2 text-xs text-navy/65 leading-relaxed">
                  {c.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16" data-testid="book-features">
        <div className="container-app grid sm:grid-cols-3 gap-4">
          {[
            {
              icon: CalendarCheck,
              t: "Free discovery available",
              d: "Every service offers a complimentary 15-min call.",
            },
            {
              icon: Clock,
              t: "Flexible times",
              d: "Daytime, evening, and select weekend slots.",
            },
            {
              icon: Phone,
              t: "Phone or video",
              d: "Choose whichever feels most comfortable for you.",
            },
          ].map((f, i) => (
            <div
              key={i}
              className="card p-6"
              data-testid={`book-feature-${i}`}
            >
              <f.icon className="w-6 h-6 text-gold mb-3" />
              <h4 className="text-lg">{f.t}</h4>
              <p className="mt-1 text-sm text-navy/65">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-20" data-testid="book-embed-section">
        <div className="container-app">
          <div className="rounded-[2rem] overflow-hidden border border-navy/5 bg-white shadow-card">
            <LazyIframe
              testId="acuity-iframe"
              title="Schedule an appointment with ConnectHER"
              src={BRAND.acuityUrl}
              height={900}
            />
          </div>
          <p className="mt-5 text-xs text-navy/50 text-center">
            Powered by Acuity Scheduling. If the scheduler does not load,
            please email{" "}
            <a
              className="text-gold"
              href={`mailto:${BRAND.emails.appointment}`}
            >
              {BRAND.emails.appointment}
            </a>
            .
          </p>
        </div>
      </section>
    </>
  );
}
